import { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import {
  setProjectId,
  getOriginalProjectData,
  fetchProject,
  resetProject,
} from 'state/Project/projectSlice';
import {
  selectShouldShowDuplicateProjectModal,
  toggleDuplicateProjectModal,
  selectIsArchiveProjectModalVisible,
  toggleArchiveProjectModal,
} from 'state/ProjectsList/projectsListSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import useConsolidatedProjectsList from 'Pages/ProjectsListPage/hooks/useConsolidatedProjectsList/useConsolidatedProjectsList';
import ProjectsTableSidePanelFilters from 'Pages/ProjectsListPage/components/ActiveProjects/components/ProjectsTableSidePanelFilters/ProjectsTableSidePanelFilters';
import ProjectSidePanel from 'Organisms/ProjectSidePanel/ProjectSidePanel';
import ConsolidatedProjects from 'Pages/ProjectsListPage/components/ConsolidatedProjects/ConsolidatedProjects';
import DuplicateProjectModal from 'Pages/ProjectsListPage/components/DuplicateProject/DuplicateProjectModal';
import ArchiveProjectModal from 'Organisms/ArchiveProjectModal/ArchiveProjectModal';
import CloseProject from 'Pages/ProjectPage/components/CloseProject/CloseProject';
import CancelProject from 'Pages/ProjectPage/components/CancelProject/CancelProject';
import PutProjectOnHoldModal from 'Pages/ProjectPage/components/PutProjectOnHoldModal/PutProjectOnHoldModal';
import { bulkUpdateProjects } from 'api/projects';

interface ConsolidatedProjectsViewProps {
  handleAddProject: () => void;
}

const ConsolidatedProjectsView: FC<ConsolidatedProjectsViewProps> = ({
  handleAddProject,
}) => {
  const dispatch = useAppThunkDispatch();
  const shouldShowDuplicateProjectModal = useSelector(
    selectShouldShowDuplicateProjectModal
  );
  const isArchiveProjectModalVisible = useSelector(
    selectIsArchiveProjectModalVisible
  );
  const selectedProject = useSelector(getOriginalProjectData);
  const { refreshProjectsList } = useConsolidatedProjectsList();

  const [displayCancelModal, setDisplayCancelModal] = useState(false);
  const [displayOnHoldModal, setDisplayOnHoldModal] = useState(false);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);

  const onCloseProjectPanel = async () => {
    await dispatch(setProjectId(''));
  };

  const onCloseDuplicateProjectModal = useCallback(() => {
    dispatch(toggleDuplicateProjectModal());
  }, [dispatch]);

  const onCloseArchiveProjectModal = useCallback(() => {
    dispatch(toggleArchiveProjectModal());
  }, [dispatch]);

  const handleOpenCancelModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCancelModal(true);
    },
    [dispatch]
  );

  const handleOpenOnHoldModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayOnHoldModal(true);
    },
    [dispatch]
  );

  const handleOpenCloseModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCloseModal(true);
    },
    [dispatch]
  );

  const handleAfterStatusChange = useCallback(() => {
    refreshProjectsList();
  }, [refreshProjectsList]);

  const handleCloseCancelModal = useCallback(() => {
    setDisplayCancelModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseOnHoldModal = useCallback(() => {
    setDisplayOnHoldModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseCloseModal = useCallback(() => {
    setDisplayCloseModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleConfirmCancelProject = useCallback(
    async (reason: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'canceled',
          cancelReason: reason,
          cancellationDate: new Date().toISOString(),
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCancelModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmOnHoldProject = useCallback(
    async (holdReason: string) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'on_hold',
          holdReason,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayOnHoldModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmCloseProject = useCallback(
    async (comments: string, closingDate: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'closed',
          closedDate: closingDate,
          finalComments: comments,
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCloseModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  return (
    <div className='relative w-full h-full px-6'>
      <ProjectSidePanel
        onClosePanel={onCloseProjectPanel}
        onSave={() => refreshProjectsList()}
      />
      <ProjectsTableSidePanelFilters
        tableName='consolidated-projects'
        isConsolidatedView
      />
      <DuplicateProjectModal
        isOpen={shouldShowDuplicateProjectModal}
        onCloseModal={onCloseDuplicateProjectModal}
        onDuplicateProject={() => refreshProjectsList()}
      />
      <ArchiveProjectModal
        shouldDisplayModal={isArchiveProjectModalVisible}
        onCloseModal={onCloseArchiveProjectModal}
        afterArchiveProject={() => refreshProjectsList()}
      />
      <ConsolidatedProjects
        handleAddProject={handleAddProject}
        onOpenCancelModal={handleOpenCancelModal}
        onOpenOnHoldModal={handleOpenOnHoldModal}
        onOpenCloseModal={handleOpenCloseModal}
      />
      <CloseProject
        confirmCloseProject={handleConfirmCloseProject}
        shouldDisplayModal={displayCloseModal}
        closeModalCallback={handleCloseCloseModal}
        project={selectedProject}
      />
      <CancelProject
        confirmCancelProject={handleConfirmCancelProject}
        shouldDisplayModal={displayCancelModal}
        closeModalCallback={handleCloseCancelModal}
        cancelReason={selectedProject?.cancel_reason}
        cancellationDate={selectedProject?.cancellation_date}
      />
      <PutProjectOnHoldModal
        onConfirm={handleConfirmOnHoldProject}
        holdReason={selectedProject?.hold_reason}
        shouldDisplayModal={displayOnHoldModal}
        onClose={handleCloseOnHoldModal}
        onCancelCallback={handleCloseOnHoldModal}
      />
    </div>
  );
};

export default ConsolidatedProjectsView;
